// Pure, framework-free logic for passkey (WebAuthn) login (see
// `features/passkey-login.md`). The cryptographic ceremonies — generating
// creation/request options and verifying attestation/assertion responses — are
// not domain concerns; they live in `server/passkeyService.ts` behind an
// injectable provider. This module owns the shared, testable rules: the
// per-user passkey cap, the challenge's lifetime and single-use semantics
// (mirroring `loginLinks.ts`), passkey naming, and the client-facing DTO that
// never carries key material.
//
// Kept import-free so it is shared by the API server (which runs the
// ceremonies) and the browser (which renders the passkey list and the
// enrolment prompt) alike.

/** How many passkeys a user may hold at once (the platform default). */
export const MAX_PASSKEYS_PER_USER = 5;

/** How long an issued WebAuthn challenge stays valid (5 minutes). */
export const PASSKEY_CHALLENGE_TTL_MS = 5 * 60 * 1000;

/** Longest accepted passkey label; anything longer is truncated, not rejected. */
export const PASSKEY_NAME_MAX_LENGTH = 60;

/**
 * The two WebAuthn ceremonies a challenge can belong to: `registration` adds a
 * passkey to the signed-in user's account; `authentication` signs in with one.
 * A challenge is only redeemable for the ceremony it was issued for.
 */
export type PasskeyCeremony = 'registration' | 'authentication';

/** Narrow a persisted ceremony string to the known values. */
export function isPasskeyCeremony(value: string): value is PasskeyCeremony {
  return value === 'registration' || value === 'authentication';
}

/**
 * A pending WebAuthn challenge. Only the keyed *hash* of the challenge is ever
 * persisted (never the challenge itself) — the same discipline as
 * `LoginLinkChallenge`. `userId` is the signed-in owner for a registration
 * ceremony and null for an authentication one (the presented credential itself
 * identifies who is signing in). `consumedAt` marks a spent (single-use)
 * challenge; `expiresAt`/`createdAt` are epoch milliseconds.
 */
export interface PasskeyChallenge {
  /** Opaque row id (a `Passkey_<uuid>`), echoed back by the client. */
  id: string;
  ceremony: PasskeyCeremony;
  userId: string | null;
  /** Keyed hash (HMAC) of the issued challenge — the only stored form. */
  challengeHash: string;
  expiresAt: number;
  /** When the ceremony completed (epoch ms); null while still pending. */
  consumedAt: number | null;
  createdAt: number;
}

/**
 * Build a fresh passkey challenge from an already-computed `challengeHash`.
 * `now` (epoch ms) is injectable for deterministic tests and sets both
 * `createdAt` and the `expiresAt = now + PASSKEY_CHALLENGE_TTL_MS` deadline.
 */
export function createPasskeyChallenge(
  id: string,
  ceremony: PasskeyCeremony,
  userId: string | null,
  challengeHash: string,
  now: number = Date.now(),
): PasskeyChallenge {
  return {
    id,
    ceremony,
    userId,
    challengeHash,
    expiresAt: now + PASSKEY_CHALLENGE_TTL_MS,
    consumedAt: null,
    createdAt: now,
  };
}

/** Whether the challenge has passed its expiry. */
export function isPasskeyChallengeExpired(
  challenge: Pick<PasskeyChallenge, 'expiresAt'>,
  now: number = Date.now(),
): boolean {
  return now >= challenge.expiresAt;
}

/** Whether the challenge may still be redeemed: neither consumed nor expired. */
export function isPasskeyChallengeUsable(
  challenge: Pick<PasskeyChallenge, 'expiresAt' | 'consumedAt'>,
  now: number = Date.now(),
): boolean {
  return challenge.consumedAt === null && !isPasskeyChallengeExpired(challenge, now);
}

/**
 * Whether the challenge may complete the given ceremony for the given caller:
 * still usable, issued for that ceremony, and — for a registration — bound to
 * the same user who is now presenting the response. An authentication
 * challenge carries no user (`userId` null on both sides).
 */
export function isPasskeyChallengeValidFor(
  challenge: Pick<PasskeyChallenge, 'expiresAt' | 'consumedAt' | 'ceremony' | 'userId'>,
  ceremony: PasskeyCeremony,
  userId: string | null,
  now: number = Date.now(),
): boolean {
  return (
    isPasskeyChallengeUsable(challenge, now) &&
    challenge.ceremony === ceremony &&
    challenge.userId === userId
  );
}

/** Whether a user holding `existingCount` passkeys may add another. */
export function canAddPasskey(existingCount: number): boolean {
  return existingCount < MAX_PASSKEYS_PER_USER;
}

/**
 * Normalise a user-supplied passkey label: trimmed and truncated to
 * `PASSKEY_NAME_MAX_LENGTH`. Empty is fine — the UI falls back to a generic
 * label — so nothing is ever rejected over a name.
 */
export function normalizePasskeyName(raw: string): string {
  return raw.trim().slice(0, PASSKEY_NAME_MAX_LENGTH);
}

/**
 * What the client sees of a registered passkey — the management list and the
 * post-login enrolment check render from this. Deliberately omits the public
 * key, counter and transports: the browser has no use for them, and the DTO
 * boundary keeps key material server-side.
 */
export interface PasskeyInfo {
  /** The credential id (base64url) — the delete handle. */
  id: string;
  /** The owner's label, possibly empty (UI falls back to a generic label). */
  name: string;
  /** Whether the credential is synced/backed up (a multi-device passkey). */
  backedUp: boolean;
  createdAt: number;
  /** Last successful sign-in with this passkey (epoch ms); null when never. */
  lastUsedAt: number | null;
}

/** Project a stored credential row onto the client-facing `PasskeyInfo`. */
export function toPasskeyInfo(credential: {
  id: string;
  name: string;
  backedUp: boolean;
  createdAt: number;
  lastUsedAt: number | null;
}): PasskeyInfo {
  return {
    id: credential.id,
    name: credential.name,
    backedUp: credential.backedUp,
    createdAt: credential.createdAt,
    lastUsedAt: credential.lastUsedAt,
  };
}
